import {EventBus, HasEvents} from '@mintdata-wsp/common/bus/EventBus';
import {FormulaEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaEngine';
import {OhmParseInputResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {MeshStateController} from '@mintdata-wsp/common/controllers/spreadsheet/mesh/MeshStateController';
import {MeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/MeshCellValueFormat';
import {IMeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormat';
import {MeshCellValueFormatKind} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormatKind';
import {MeshCellTextWrapping} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellTextAlign';
import {IFocusActivator} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FocusActivator';
import {
    FormulaInputTokenType,
    ILinearFormulaToken
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {MeshCellNote} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {
    DefaultMeshCellSettings,
    MeshCellSettings,
    MeshCellSettingsProps
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSize';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {AnySheet, AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {EMPTY_SLOT_FORMULA, SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {MeshLocationUtils} from '@mintdata-wsp/common/util/MeshLocationUtils';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {TextSelection} from '@mintdata/mint-kit/api/models/Text';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';
import bind from 'bind-decorator';
import {action} from 'mobx';

export const ARRAY_FORMULA_NAME:string = 'ARRAYFORMULA';

type MeshControllerListener = {
    onMouseMove():void;
}

export class MeshController extends MeshStateController implements HasEvents<MeshControllerListener>
{
    public focusActiveSheet():void
    {
        const {main} = controllers.ui.spreadsheets;

        const activeSheet = main.getActiveSheet();
        if( activeSheet )
            this.focusSheetById(activeSheet.id);
    }

    public focusSheetById(sheetId:SheetId):void
    {
        const {mesh, workbook} = controllers.ui.spreadsheets;

        mesh.focusMesh(
            workbook.getMeshContextBySheetId(sheetId)
        );
    }

    public focusMesh(ctx:IWSPMeshContext):void
    {
        const {mesh, tabs} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( meshState
            && meshState.details
            && meshState.details.viewFocusActivator )
        {
            tabs.setActiveSheet(ctx.workbookId, ctx.mainSheetId);
            meshState.details.viewFocusActivator.focus();
        }
    }

    public focusMeshOrControl(ctx:IWSPMeshContext):void
    {
        const {spreadsheets} = controllers.ui;

        const meshState = spreadsheets.mesh.getState(ctx);

        const {isEditNoteMode} = meshState.details;

        const {isEditMode} = spreadsheets.main;

        if( isEditMode )
        {
            const {meshInput} = controllers.ui.spreadsheets;
            const inputState = spreadsheets.main.getEditorState().input;

            meshInput.focusInputControl();
            meshInput.selectTextInInputControl(inputState.textSelection);
        }
        else if( isEditNoteMode )
        {
            const {meshNote} = controllers.ui.spreadsheets;

            meshNote.focusNoteControl(ctx);
        }
        else
        {
            this.focusMesh(ctx);
        }
    }

    @bind
    public onFocusMesh(ctx:IWSPMeshContext):void
    {
        const {main} = controllers.ui.spreadsheets;
        const {workbookId, sheetId} = ctx;

        const workbook:Workbook = main.findWorkbookById(workbookId);
        workbook.view.setFocusedSheetId(sheetId);
    }

    @action
    public setMeshVisibleSize(ctx:IWSPMeshContext, visibleWidth:number, visibleHeight:number):void
    {
        const {mesh, main} = controllers.ui.spreadsheets;
        const {workbookId} = ctx;
        const workbook:Workbook = main.findWorkbookById(workbookId);
        const sheets = workbook.runtime.sheets;

        for( const sheet of sheets )
        {
            const meshState = mesh.getState({...ctx, sheetId: sheet.id});
            const {details} = meshState;

            if( details.visibleWidth !== visibleWidth )
                details.visibleWidth = visibleWidth;

            if( details.visibleHeight !== visibleHeight )
                details.visibleHeight = visibleHeight;
        }
    }

    public onMouseMoveEvent()
    {
        EventBus.fire(this, 'mouseMove');
    }

    @action
    public setNestedMeshVisibleSize(ctx:IWSPMeshContext, visibleWidth:number, visibleHeight:number):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( meshState && meshState.details )
        {
            const {details} = meshState;

            if( details.visibleWidth !== visibleWidth )
                details.visibleWidth = visibleWidth;

            if( details.visibleHeight !== visibleHeight )
                details.visibleHeight = visibleHeight;
        }
    }

    @action
    public setViewFocusActivator(ctx:IWSPMeshContext, isNested:boolean, viewFocusActivator:IFocusActivator):void
    {
        const {mesh, main} = controllers.ui.spreadsheets;
        const {workbookId, sheetId} = ctx;

        if( isNested )
        {
            const meshState:MeshState = mesh.getState(ctx);
            if( meshState )
            {
                meshState.details.viewFocusActivator = viewFocusActivator;
                if( viewFocusActivator )
                {
                    viewFocusActivator.focus();
                }
            }
        }
        else
        {
            const workbook:Workbook = main.findWorkbookById(workbookId);
            const sheets = workbook.runtime.sheets;

            for( const sheet of sheets )
            {
                const meshState = mesh.getState({...ctx, sheetId: sheet.id});
                const {details} = meshState;

                details.viewFocusActivator = viewFocusActivator;

                if( sheet.id === sheetId && viewFocusActivator )
                {
                    viewFocusActivator.focus();
                }
            }
        }
    }

    @action
    public setMeshScrollPosition(ctx:IWSPMeshContext, scrollLeft:number, scrollTop:number, fireEvent:boolean = true):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( meshState && meshState.details )
        {
            if( scrollLeft != null && meshState.details.scrollLeft !== scrollLeft )
            {
                meshState.details.scrollLeft = scrollLeft;

                if( fireEvent )
                    EventBus.fire(this, 'sheetScrolled');
            }

            if( scrollTop != null && meshState.details.scrollTop !== scrollTop )
            {
                meshState.details.scrollTop = scrollTop;

                if( fireEvent )
                    EventBus.fire(this, 'sheetScrolled');
            }
        }
    }

    public convertR1C1toA1(formula:SlotFormula, location:SlotLocation):SlotFormula
    {
        const engine = FormulaEngine.getInstance();
        const ohmResult:OhmParseInputResult = engine.parseInputRC(formula, location);

        const fic = controllers.ui.spreadsheets.formulaInput;
        const formulaTokens:ILinearFormulaToken[] = fic.getPlainFormulaTokens(ohmResult.result);
        const formulaTokensText = fic.getFormulaTokensText(formulaTokens, location.colIndex, location.rowIndex, true);

        return formulaTokensText;
    }

    public formatValue(value:IValue, location:SlotLocation, sheetId:SheetId):SlotFormula
    {
        const sheetSettingsController = controllers.ui.spreadsheets.sheetSettings;
        const cellFormat = sheetSettingsController.getCellFormat(sheetId, location.toKey());
        const kind = cellFormat.kind;
        if( kind == MeshCellValueFormatKind.Date || kind == MeshCellValueFormatKind.DateTime ||
            kind == MeshCellValueFormatKind.Percent || kind == MeshCellValueFormatKind.Time ||
            kind == MeshCellValueFormatKind.Duration )
        {
            return cellFormat.formatValue(value);
        }
        return value.toString();
    }

    private _isEditingCellAllowed(ctx:IWSPMeshContext):boolean
    {
        const {mesh, sheetSettings} = controllers.ui.spreadsheets;
        const {sheetId} = ctx;
        const meshState = mesh.getState(ctx);
        const {activeCell} = meshState.details;

        if( meshState.details.isReadonly )
        {
            mk.warn(area.Slot, `sheet is readonly: ${sheetId}`);
            return false;
        }

        const cellSettings = sheetSettings.getCellSettings(sheetId, activeCell.asKey());

        if( cellSettings && cellSettings.isValueMasked )
        {
            mk.warn(area.Slot, `slot is in secure mode: ${sheetId}`);
            return false;
        }

        return true;
    }

    @action.bound
    public startEditingCell(ctx:IWSPMeshContext, initialChar?:string, cursorPosition?:number, isShowDoc?:boolean):void
    {
        const {mesh, meshSelection} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {activeCell} = meshState.details;

        meshSelection.checkActiveCellCol(ctx, activeCell.colIndex);
        meshSelection.checkActiveCellRow(ctx, activeCell.rowIndex);

        this._startEditingCell(ctx, initialChar, cursorPosition, isShowDoc);
    }

    @action.bound
    private _startEditingCell(ctx:IWSPMeshContext, initialChar?:string, cursorPosition?:number, isShowDoc?:boolean):void
    {
        const {mesh, main} = controllers.ui.spreadsheets;
        const {sheetCtrl, sheetId} = ctx;
        const meshState = mesh.getState(ctx);
        const inputState = main.getEditorState().input;
        const {activeCell, activeCellBounds} = meshState.details;

        if( !this._isEditingCellAllowed(ctx) )
            return;

        inputState.activeCellBounds = activeCellBounds.clone();
        inputState.colIndex = activeCell.colIndex;
        inputState.rowIndex = activeCell.rowIndex;
        inputState.isValueEdited = false;

        const location = new SlotLocation(activeCell.colIndex, activeCell.rowIndex);
        let curFormula:SlotFormula;

        if( initialChar )
            curFormula = initialChar;
        else
        {
            curFormula = sheetCtrl.getSlotFormula(sheetId, location);

            if( !curFormula )
                curFormula = EMPTY_SLOT_FORMULA;
            else if( typeof curFormula !== 'string' )
            {
                curFormula = this.formatValue(curFormula, location, sheetId);
            }
            else
                curFormula = this.convertR1C1toA1(curFormula, location);
        }

        main.setEditMode(true, ctx.sheetId);

        requestAnimationFrame(() =>
        {
            const isUserInput = initialChar != null;
            const startIndex = initialChar != null ? initialChar.length : (curFormula as string).length;
            const endIndex = null;

            const textSelection = cursorPosition !== undefined
                ? new TextSelection(cursorPosition, cursorPosition)
                : new TextSelection(startIndex, endIndex);

            const mic = controllers.ui.spreadsheets.meshInput;
            mic.setActiveCellFormula(ctx, curFormula, textSelection, isUserInput);
            mic.focusInputControl();

            if( isShowDoc )
                mic.clearTypeaheadItems();
        });
    }

    @action.bound
    public startEditingOrCreateNote(ctx:IWSPMeshContext):void
    {
        const {mesh, meshSelection} = controllers.ui.spreadsheets;
        const {sheetId} = ctx;
        const meshState = mesh.getState(ctx);
        const {activeCell} = meshState.details;

        if( meshState.details.isReadonly )
        {
            mk.warn(area.Slot, `sheet is readonly: ${sheetId}`);
            return;
        }

        meshSelection.checkActiveCellCol(ctx, activeCell.colIndex);
        meshSelection.checkActiveCellRow(ctx, activeCell.rowIndex);

        this._startEditingOrCreateNote(ctx);
    }

    @action.bound
    public _startEditingOrCreateNote(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const {sheetSettingsCtrl, sheetId} = ctx;
        const meshState = mesh.getState(ctx);
        const noteState = meshState.note;
        const {activeCell, activeCellBounds} = meshState.details;

        const location = new MeshLocation(activeCell.colIndex, activeCell.rowIndex).asKey();

        noteState.activeCellBounds = activeCellBounds.clone();
        noteState.colIndex = activeCell.colIndex;
        noteState.rowIndex = activeCell.rowIndex;
        noteState.note = sheetSettingsCtrl.getCellNote(sheetId, location) || MeshCellNote.create();
        meshState.details.isEditNoteMode = true;

        requestAnimationFrame(() =>
        {
            const {meshNote} = controllers.ui.spreadsheets;

            // meshNote.setActiveCellNote(ctx, curNote);
            meshNote.focusNoteControl(ctx);
        });
    }

    @action.bound
    public removeNote(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const {sheetSettingsCtrl, sheetId} = ctx;
        const meshState = mesh.getState(ctx);
        const {activeCell} = meshState.details;

        if( meshState.details.isReadonly )
        {
            mk.warn(area.Slot, `sheet is readonly: ${sheetId}`);
            return;
        }

        const cellKey = new MeshLocation(activeCell.colIndex, activeCell.rowIndex).asKey();
        let note = sheetSettingsCtrl.getCellNote(sheetId, cellKey);

        if( !note ) return;

        requestAnimationFrame(() =>
        {
            const {meshNote} = controllers.ui.spreadsheets;

            this.closeNote(ctx);
            meshNote.removeNote(ctx, cellKey);
        });
    }

    @action
    public finishEditingNote(ctx:IWSPMeshContext):void
    {
        const {meshNote} = controllers.ui.spreadsheets;

        meshNote.applyNote(ctx);

        this.closeNote(ctx);
    }

    @action
    public closeNote(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        meshState.details.isEditNoteMode = false;
        this.focusMeshOrControl(ctx);
    }

    @action
    public closeNoteIfSelectCell(ctx:IWSPMeshContext, newLocation:MeshLocation):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        // TODO: ???
        // const {colIndex, rowIndex} = meshState.note;
        // if( rowIndex === newLocation.rowIndex && colIndex === newLocation.colIndex ) return;

        if( meshState.details.isEditNoteMode )
            this.finishEditingNote(ctx);
    }

    @action
    public commitEditingCellIfValid(ctx:IWSPMeshContext):void
    {
        const {main, mesh} = controllers.ui.spreadsheets;
        
        Assert.yes(main.isEditMode);

        if( main.getEditorState().input.isValueEdited )
            mesh.finishEditingCell(ctx);
        else
            mesh.abortEditingCell(ctx);
    }

    @action
    public finishEditingCell(ctx:IWSPMeshContext, arrayFormula:boolean = false):void
    {
        const {main, mesh, formulaInput} = controllers.ui.spreadsheets;
        
        const meshState = mesh.getState(ctx);
        
        const {sheetCtrl} = ctx;

        const {editSheetId, input} = main.getEditorState();

        const {colIndex, rowIndex, formulaTokens} = input;

        const updatedTokens = formulaInput.normalizeCellReferences(formulaTokens, colIndex, rowIndex);
        let updatedFormula:SlotFormula = formulaInput.getFormulaTokensText(updatedTokens, colIndex, rowIndex, false);

        const slotLocation:SlotLocation = new SlotLocation(colIndex, rowIndex);
        const oldFormula:SlotFormula = sheetCtrl.getSlotFormula(editSheetId, slotLocation);

        if( updatedFormula !== oldFormula )
        {
            if( arrayFormula )
            {
                const arrayFormulaApplied = this.isArrayFormulaApplied(updatedTokens);
                if( !arrayFormulaApplied )
                {
                    const formulaStartText = this.getStartFormulaText(updatedTokens);
                    if( formulaStartText.length > 0 )
                    {
                        const {selectedRange} = meshState.details;

                        const rows = Math.abs(selectedRange.to.rowIndex - selectedRange.from.rowIndex) + 1;
                        const columns = Math.abs(selectedRange.to.colIndex - selectedRange.from.colIndex) + 1;

                        updatedFormula = `${formulaStartText}${ARRAY_FORMULA_NAME}(${updatedFormula.substr(formulaStartText.length)}, ${rows}, ${columns})`;
                    }
                }
            }

            sheetCtrl.setSlotFormula(editSheetId, slotLocation, updatedFormula);
        }

        this.abortEditingCell(ctx);

        setTimeout(action(() =>
        {
            controllers.ui.spreadsheets.mesh.calcRowHeight(ctx, new MeshLocation(colIndex, rowIndex));
        }), 0);
    }

    private getStartFormulaText(updatedTokens:Array<ILinearFormulaToken>):string
    {
        const formulaToken = updatedTokens.find(value =>
            value.token.type == FormulaInputTokenType.FormulaStart
            || value.token.type == FormulaInputTokenType.FormulaLiveStart
            || value.token.type == FormulaInputTokenType.FormulaLazyStart
        );

        if( formulaToken )
            return formulaToken.token.value;

        return '';
    }

    private isArrayFormulaApplied(updatedTokens:Array<ILinearFormulaToken>):boolean
    {
        const formulaToken = updatedTokens.find(value => value.token.type == FormulaInputTokenType.SimpleFunctionName);

        if( formulaToken )
            return formulaToken.token.value.toUpperCase() === ARRAY_FORMULA_NAME;

        return false;
    }

    @action.bound
    public abortEditingCell(ctx:IWSPMeshContext):void
    {
        const {main, meshSelection, tabs, workbook} = controllers.ui.spreadsheets;

        const {editSheetId, input} = main.getEditorState();

        main.setEditMode(false);

        if( editSheetId !== ctx.sheetId )
        {
            tabs.setActiveSheet(ctx.workbookId, editSheetId);
        }

        const newCtx:IWSPMeshContext = workbook.getMeshContextBySheetId(editSheetId);

        this.focusMesh(newCtx);
        this.onFocusMesh(newCtx);

        meshSelection.checkActiveCellCol(newCtx, input.colIndex);
        meshSelection.checkActiveCellRow(newCtx, input.rowIndex);
    }

    @action.bound
    public evaluateSelectedCells(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {sheetId, sheetCtrl} = ctx;

        if( !meshState.details.selectedRange )
            return;

        const {size: {numCols, numRows}} = meshState;

        // TODO: Send single reEvaluate request for Cloud Sheet
        MeshUtils.eachCellInRange(meshState.details.selectedRange, numRows, numCols, (cellKey:string) =>
        {
            const slotLocation = MeshLocation.fromKey(cellKey).toSlotLocation();
            const slot = sheetCtrl.findSlot(sheetId, slotLocation);

            if( !slot || sheetCtrl.isSlotLoading(sheetId, slot.location) )
                return;

            slot.isLazy
                ? sheetCtrl.reEvaluateLazySlot(sheetId, slotLocation)
                : sheetCtrl.reEvaluateSlot(sheetId, slotLocation);
        });
    }

    @action.bound
    public abortSelectedCellsEvaluation(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {sheetId, sheetCtrl} = ctx;

        if( !meshState.details.selectedRange )
        {
            return;
        }

        const {size: {numCols, numRows}} = meshState;

        MeshUtils.eachCellInRange(meshState.details.selectedRange, numRows, numCols, (cellKey:string) =>
        {
            const slotLocation = MeshLocation.fromKey(cellKey).toSlotLocation();

            sheetCtrl.abortSlotEvaluation(sheetId, slotLocation);
        });
    }

    public clearSelectedCells(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( !meshState.details.selectedRange )
            return;

        this.clearCells(ctx, meshState.details.selectedRange, false);

        mesh.calcRowHeight(ctx);
    }

    public clearSelectedCellValues(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( !meshState.details.selectedRange )
            return;

        this.clearCellValues(ctx, meshState.details.selectedRange);

        mesh.calcRowHeight(ctx);
    }

    public toggleSelectedCellsLaziness(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const {sheetId, sheetCtrl} = ctx;

        const meshState = mesh.getState(ctx);

        const {details: {activeCell, selectedRange}} = meshState;

        if( !selectedRange )
            return;

        const activeSlot:AnySlot = sheetCtrl.findSlot(sheetId, activeCell.toSlotLocation());
        const makeLazy:boolean = !activeSlot || !activeSlot.isLazy;

        makeLazy
            ? this.makeCellsLazy(ctx, selectedRange)
            : this.makeCellsRegular(ctx, selectedRange);
    }

    public getCellSize(ctx:IWSPMeshContext, location:MeshLocation):MeshSize
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( !location )
            return;

        return MeshSize.from(
            meshState.colWidths[location.colIndex],
            meshState.rowHeights[location.rowIndex]
        );
    }

    @action
    public resizeColumnToContent(ctx:IWSPMeshContext, colIndex:number, isNested:boolean, isFirstView:boolean = false):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( !meshState ) return;

        let {selectedRange} = meshState.details;

        const fullColsOrTableSelected =
            (selectedRange.from.rowIndex === null && selectedRange.to.rowIndex === null) ||
            (selectedRange.from.rowIndex === 0 && selectedRange.to.rowIndex === meshState.size.numRows - 1);

        let colFrom:number = colIndex;
        let colTo:number = colIndex;

        if( fullColsOrTableSelected && colIndex >= selectedRange.from.colIndex && colIndex <= selectedRange.to.colIndex )
        {
            colFrom = selectedRange.from.colIndex;
            colTo = selectedRange.to.colIndex;
        }

        let rowsForCalcHeight:number[] = [];

        for( let _colIndex = colFrom; _colIndex <= colTo; _colIndex++ )
        {
            let isColWidthChanged = this._isCalcColWidthAndSet(ctx, _colIndex, isNested, isFirstView);

            if( isColWidthChanged )
            {
                const _rowsForCalcHeight:number[] = this._getRowsInColumnWithWrapTextInSettings(ctx, colIndex, isFirstView);

                _rowsForCalcHeight.forEach((rowIndex:number) =>
                {
                    if( rowsForCalcHeight.indexOf(rowIndex) === -1 ) rowsForCalcHeight.push(rowIndex);
                });
            }
        }

        rowsForCalcHeight.forEach((rowIndex:number) =>
        {
            this.calcRowHeight(ctx, MeshRange.ofEntireRow(rowIndex));
        });
    }

    @action
    public calcRowHeightOnlyForWrappedCellsInColumn(ctx:IWSPMeshContext, colIndex:number):void
    {
        const rowsForCalcHeight:number[] = this._getRowsInColumnWithWrapTextInSettings(ctx, colIndex);

        rowsForCalcHeight.forEach((rowIndex:number) =>
        {
            this.calcRowHeight(ctx, MeshRange.ofEntireRow(rowIndex));
        });
    }

    private _getRowsInColumnWithWrapTextInSettings(ctx:IWSPMeshContext, colIndex:number, isFirstView:boolean = false):number[]
    {
        const {mesh} = controllers.ui.spreadsheets;
        const {sheetCtrl, sheetId, sheetSettingsCtrl} = ctx;
        const meshState = mesh.getState(ctx);
        const {size: {numRows}} = meshState;

        let rowsForCalcHeight:number[] = [];

        const {visibleStartRowIndex, visibleEndRowIndex} = meshState;
        const maxRows = isFirstView ? visibleEndRowIndex - visibleStartRowIndex + 1 : numRows;

        for( let rowIndex = 0; rowIndex < maxRows; rowIndex++ )
        {
            const slot:AnySlot = sheetCtrl.findSlot(sheetId, new SlotLocation(colIndex, rowIndex));

            if( !slot ) continue;

            if( !slot.containsFrame && !(slot.isLazy && !slot.value) && slot.valueAsString )
            {
                const cellLocation:MeshLocation = new MeshLocation(colIndex, rowIndex);
                const cellSettings:MeshCellSettings = sheetSettingsCtrl.getCellSettings(sheetId, cellLocation.asKey());

                if( cellSettings && cellSettings.wrapText )
                {
                    rowsForCalcHeight.push(rowIndex);
                }
            }
        }

        return rowsForCalcHeight;
    }

    private _isCalcColWidthAndSet(ctx:IWSPMeshContext, colIndex:number, isNested:boolean, isFirstView:boolean):boolean
    {
        const {mesh, sheetSettings} = controllers.ui.spreadsheets;
        const {sheetCtrl, sheetId, sheetSettingsCtrl} = ctx;
        const meshState = mesh.getState(ctx);
        const {size: {numRows}} = meshState;

        let needChangeWidth = false;

        const HORIZONTAL_CELL_LEFT_PADDING = 7;
        const HORIZONTAL_CELL_RIGHT_PADDING = 8;

        const defaultFont = '12px arial, sans-serif';

        const columnName = this._getColumnName(sheetId, colIndex);
        const columnNameWidth = DomUtils.getTextWidth(columnName, defaultFont);

        let maxWidth:number = columnNameWidth;

        const {visibleStartRowIndex, visibleEndRowIndex} = meshState;
        const maxRows = isFirstView ? visibleEndRowIndex - visibleStartRowIndex + 1 : numRows;

        for( let rowIndex = 0; rowIndex < maxRows; rowIndex++ )
        {
            const slot:AnySlot = sheetCtrl.findSlot(sheetId, new SlotLocation(colIndex, rowIndex));

            if( !slot ) continue;

            let slotText:string;
            let extraPixels = 0;
            let fontString:string;
            let cellSettings:MeshCellSettings;

            const EXTRA_PIXELS_FOR_LAZY = 16 /* play btn width */ + 4 /* margin */ + 10 /* edit btn width */ + 2 /* right */;

            if( slot.isLazy )
                extraPixels += EXTRA_PIXELS_FOR_LAZY;

            if( slot.containsFrame )
            {
                const frame = slot.value as IFrame;
                slotText = `${frame.recordCount}${frame.recordCount === 1 ? 'row' : 'rows'}`;
                extraPixels += 10 /* counter box padding */ + 4 /* margin */;
                fontString = defaultFont;
            }
            else if( slot.isLazy && !slot.value )
            {
                slotText = slot.status.toString();
                fontString = defaultFont;
            }
            else if( slot.valueAsString )
            {
                slotText = slot.valueAsString;

                // if( !isNested )
                // {
                    const settings = sheetSettings.getSheetSettings(sheetId);

                    if( settings )
                    {
                        const cellLocation = new MeshLocation(colIndex, rowIndex);
                        cellSettings = sheetSettingsCtrl.getCellSettings(sheetId, cellLocation.asKey());

                        const valueFormatter:IMeshCellValueFormat = this._getValueFormatter(ctx, slot, cellSettings);
                        const value:JSX.Element | string = valueFormatter.formatValue(slot.value);

                        slotText = value.toString();

                        if( cellSettings && cellSettings.wrapText )
                        {
                            const paddings = 7 /* cell padding-left */ + 8 /* cell padding-right */;
                            const colWidth = meshState.colWidths[colIndex] - paddings;
                            const textWidth = DomUtils.getTextWidth(slotText, null, cellSettings);

                            maxWidth = Math.max(maxWidth, textWidth < colWidth ? textWidth : colWidth);
                            needChangeWidth = true;
                            continue;
                        }
                    }
                // }
            }
            else continue;

            maxWidth = Math.max(
                maxWidth,
                DomUtils.getTextWidth(slotText, fontString, cellSettings || {}) + extraPixels
            );
            needChangeWidth = true;
        }

        if( needChangeWidth )
        {
            const oldColWidth = meshState.colWidths[colIndex];
            const fullColWidth = maxWidth + HORIZONTAL_CELL_LEFT_PADDING + HORIZONTAL_CELL_RIGHT_PADDING + 1; // 1 under right border

            const maxColumnWidth = meshState.details.visibleWidth * 0.8;

            const newColWidth = Math.ceil(isNested ? Math.min(fullColWidth, maxColumnWidth) : fullColWidth);

            if( oldColWidth == newColWidth ) return;

            meshState.setColWidth(colIndex, newColWidth);
            return true;
        }
    }

    private _getColumnName(sheetId:SheetId, colIndex:number):string
    {
        const sheet:AnySheet = controllers.ui.spreadsheets.main.findAnySheetById(sheetId, true);

        if( sheet.columnNames && colIndex >= 0 && colIndex < sheet.columnNames.length )
            return sheet.columnNames[colIndex];
        else
            return MeshLocationUtils.getLetterByColumnIndex(colIndex);
    }

    private _getValueFormatter(ctx:IWSPMeshContext, slot:AnySlot, cellSettings:MeshCellSettings):IMeshCellValueFormat
    {
        const {colIndex, rowIndex} = slot.location;
        const {sheetSettingsCtrl, sheetId} = ctx;

        const cellFormat = sheetSettingsCtrl.getCellFormat(sheetId, new MeshLocation(colIndex, rowIndex).asKey());

        let valueFormatter:IMeshCellValueFormat;

        if( cellFormat )
        {
            if( cellSettings && cellSettings.isValueMasked )
                valueFormatter = MeshCellValueFormat.MaskedText;
            else if( this._isSlotInvalid(slot) )
                valueFormatter = MeshCellValueFormat.General;
            else
                valueFormatter = cellFormat;
        }
        else
        {
            valueFormatter = MeshCellValueFormat.General;
        }

        return valueFormatter;
    }

    private _isSlotInvalid(slot:AnySlot):boolean
    {
        if( slot && slot.value != null )
        {
            const type:Type = slot.value.getType();

            if( type.isEqualTo(Type.Error) )
                return true;
        }

        return false;
    }

    @action
    public calcRowHeightDblClick(ctx:IWSPMeshContext, rowIndex:number):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        let {selectedRange} = meshState.details;

        const fullRowsOrTableSelected =
            (selectedRange.from.colIndex === null && selectedRange.to.colIndex === null) ||
            (selectedRange.from.colIndex === 0 && selectedRange.to.colIndex === meshState.size.numCols - 1);

        if( fullRowsOrTableSelected )
        {
            let rowFrom = selectedRange.from.rowIndex;
            let rowTo = selectedRange.to.rowIndex;

            if( rowIndex >= rowFrom && rowIndex <= rowTo )
            {
                this.calcRowHeight(ctx);
                return;
            }
        }

        // single row dbl clicked
        // or rowIndex outside selectedRange
        this.calcRowHeight(ctx, MeshRange.ofEntireRow(rowIndex));
    }

    @action
    public calcRowHeight(ctx:IWSPMeshContext, cellLocation?:MeshLocation | MeshRange):void
    {
        const {mesh, sheetSettings} = controllers.ui.spreadsheets;
        const {sheetCtrl, sheetId, sheetSettingsCtrl} = ctx;
        const meshState = mesh.getState(ctx);

        let rowFrom;
        let rowTo;

        if( cellLocation instanceof MeshLocation )
        {
            // single cell
            if( typeof cellLocation.colIndex === 'number' && typeof cellLocation.rowIndex === 'number' )
            {
                rowFrom = rowTo = cellLocation.rowIndex;
            }
            // column
            else if( cellLocation.colIndex && cellLocation.rowIndex === null )
            {
                rowFrom = 0;
                rowTo = meshState.size.numRows - 1;
            }
        }
        else if( cellLocation instanceof MeshRange )
        {
            let cutRange = cellLocation;
            cutRange = this._getNormalizedRange(cutRange, meshState);

            rowFrom = cutRange.from.rowIndex;
            rowTo = cutRange.to.rowIndex;
        }
        else
        {
            let {selectedRange} = meshState.details;
            selectedRange = this._getNormalizedRange(selectedRange, meshState);

            rowFrom = selectedRange.from.rowIndex;
            rowTo = selectedRange.to.rowIndex;
        }

        const fullRowsRange = this._getNormalizedRange(
            this._makeRange(null, rowFrom, null, rowTo),
            meshState
        );

        let currentRow:number = rowFrom;
        let maxFontSize:number = 0;
        let maxCountOfLines:number = 1;
        let maxWrapText:number = 0;

        MeshUtils.eachCellInRange(fullRowsRange, rowTo - rowFrom + 1, meshState.size.numCols, (cellKey:string) =>
        {
            const location:MeshLocation = MeshLocation.fromKey(cellKey);

            // next row
            if( currentRow !== location.rowIndex )
            {
                const oldRowHeight = meshState.rowHeights[currentRow];
                const newRowHeight = this._calcCellHeight(maxFontSize, maxCountOfLines, maxWrapText);

                if( oldRowHeight !== newRowHeight )
                    meshState.setRowHeight(currentRow, newRowHeight);

                currentRow = location.rowIndex;
                maxFontSize = 0;
                maxCountOfLines = 1;
                maxWrapText = 0;
            }

            const slot:AnySlot = sheetCtrl.findSlot(sheetId, location.toSlotLocation());

            if( this._canCalcCellHeight(slot) )
            {
                let cellFontSize:number = DefaultMeshCellSettings[MeshCellSettingsProps.FontSize];
                let countOfLines:number = 1;

                const cellSettings = sheetSettingsCtrl.getCellSettings(
                    sheetId,
                    new MeshLocation(location.colIndex, location.rowIndex).asKey()
                );

                // ignore cellSettings if !(...)
                if( !(slot.containsFrame || (slot.isLazy && !slot.value)) )
                {
                    const slotText:string = slot.valueAsString;
                    countOfLines = slotText.split(DomUtils.NEW_LINE_CHAR).length || 1;

                    if( cellSettings && cellSettings.wrapText === MeshCellTextWrapping.Wrap )
                    {
                        const paddings = 7 /* cell padding-left */ + 8 /* cell padding-right */;
                        const colWidth = meshState.colWidths[location.colIndex] - paddings;

                        const cellValueHeight = DomUtils.getTextHeight(slotText, colWidth, null, cellSettings);

                        maxWrapText = Math.max(maxWrapText, cellValueHeight);
                    }
                    if( cellSettings && cellSettings.fontSize )
                    {
                        cellFontSize = cellSettings.fontSize;
                    }
                }

                maxFontSize = Math.max(maxFontSize, cellFontSize);
                maxCountOfLines = Math.max(maxCountOfLines, countOfLines);
            }

            // last cell
            if( location.rowIndex == fullRowsRange.to.rowIndex && location.colIndex == fullRowsRange.to.colIndex )
            {
                const oldRowHeight = meshState.rowHeights[currentRow];
                const newRowHeight = this._calcCellHeight(maxFontSize, maxCountOfLines, maxWrapText);

                if( oldRowHeight !== newRowHeight )
                    meshState.setRowHeight(currentRow, newRowHeight);
            }
        });
    }

    public getContextOfFocusedSheet():IWSPMeshContext | null
    {
        const {main, workbook: wb} = controllers.ui.spreadsheets;

        const workbook = main.getRootWorkbook();
        if( !workbook ) return null;

        return wb.getMeshContextBySheetId(workbook.view.focusedSheetId);
    }

    public getState(ctx:IWSPMeshContext):MeshState
    {
        const {main, workbook, tabs, frameDetails} = controllers.ui.spreadsheets;

        const {sheetId} = ctx;
        const sheet:AnySheet = main.findAnySheetById(sheetId, true);

        if( !sheet )
        {
            // console.warn(`Could not find sheet by id`, sheetId);
            return null;
        }

        if( workbook.isTopLevelSheet(sheet.workbook.id, sheetId) )
        {
            const workbook = main.findWorkbookById(sheet.workbook.id);
            return workbook.view.getMeshStateBySheetId(sheet.id);
        }
        else
        {
            const activeSheet = tabs.getActiveSheet(sheet.workbook.id);
            const frameDetailsEntry = frameDetails.getEntryBySheetId(sheet.workbook.id, activeSheet.id, sheetId);

            return frameDetailsEntry && frameDetailsEntry.meshState;
        }
    }

    private _canCalcCellHeight(slot:AnySlot):boolean
    {
        if( !slot ) return false;

        if( slot.containsFrame || (slot.isLazy && !slot.value) ) return true;

        if( !slot.valueAsString ) return false;

        return true;
    }

    private _calcCellHeight(fontSize:number, countOfLines:number, maxWrapText:number):number
    {
        if( !fontSize )
            fontSize = DefaultMeshCellSettings[MeshCellSettingsProps.FontSize];

        const paddings = 7 /* cell padding-top */ + 8 /* cell padding-bottom */;

        return Math.max(
            Math.round(fontSize * 1.33333 /* pt -> px */ * 1.28581 /* body line-height */ * countOfLines),
            maxWrapText
        ) + paddings;
    }

    private _makeRange(colFrom:number, rowFrom:number, colTo:number, rowTo:number):MeshRange
    {
        return new MeshRange(
            new MeshLocation(colFrom, rowFrom),
            new MeshLocation(colTo, rowTo)
        );
    }

    private _getNormalizedRange(range:MeshRange, meshState:MeshState):MeshRange
    {
        let colFrom = range.from.colIndex;
        let rowFrom = range.from.rowIndex;
        let colTo = range.to.colIndex;
        let rowTo = range.to.rowIndex;

        if( colFrom === null ) colFrom = 0;
        if( rowFrom === null ) rowFrom = 0;

        if( colTo === null ) colTo = meshState.size.numCols - 1;
        if( rowTo === null ) rowTo = meshState.size.numRows - 1;

        return this._makeRange(colFrom, rowFrom, colTo, rowTo);
    }
}
